import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [qSearch, setQSearch] = useState("");
  const [loading, setLoading] = useState(false);

  
  useEffect(() => {
    const token = localStorage.getItem("membreToken");

    if (!token) {
      navigate("/membre/login");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime || decoded.role !== "admin") {
        localStorage.removeItem("membreToken");
        localStorage.removeItem("membre");
        navigate("/membre/login");
      }
    } catch {
      localStorage.removeItem("membreToken");
      navigate("/membre/login");
    }
  }, [navigate]);

  
  async function load() {
    setLoading(true);

    let query = supabase
      .from("subscriptions")
      .select("*")
      .order("created_at", { ascending: false });

    if (filter === "approved") query = query.eq("approved", true);
    if (filter === "pending") query = query.eq("approved", false);
    if (qSearch.trim()) query = query.ilike("email", `%${qSearch}%`);

    const { data } = await query;
    setSubs(data || []);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [filter, qSearch]);

  

  
  const stats = useMemo(() => {
    return {
      total: subs.length,
      approved: subs.filter((s) => s.approved).length,
      pending: subs.filter((s) => !s.approved).length,
    };
  }, [subs]);

  
  const chartData = useMemo(() => {
    const days = Array.from({ length: 7 }).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return {
        key: d.toISOString().slice(0, 10),
        count: 0,
      };
    });

    subs.forEach((s) => {
      const k = s.created_at.slice(0, 10);
      const d = days.find((x) => x.key === k);
      if (d) d.count++;
    });

    return {
      labels: days.map((d) => d.key),
      datasets: [
        {
          label: "Nouveaux abonnés",
          data: days.map((d) => d.count),
          borderColor: "#2563eb",
          backgroundColor: "rgba(37, 99, 235, .2)",
          tension: 0.3,
        },
      ],
    };
  }, [subs]);

  return (
    <div className="p-4 space-y-6">

      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">

        <h1 className="text-2xl font-bold dark:text-white">
          Tableau de bord
        </h1>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto">

          
          <button
            onClick={() => navigate("/")}
            className="
              px-4 py-2
              bg-yellow-500
              text-white
              font-semibold
              rounded-lg
              hover:bg-yellow-600
              transition
            "
          >
            ⬅ Retour à l’accueil
          </button>

          <input
            placeholder="Rechercher..."
            className="px-3 py-2 border rounded-lg 
                       dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            value={qSearch}
            onChange={(e) => setQSearch(e.target.value)}
          />

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg 
                       dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="all">Tous</option>
            <option value="approved">Approuvés</option>
            <option value="pending">En attente</option>
          </select>

        </div>
      </div>

      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-lg shadow bg-white dark:bg-gray-900">
          <p className="text-gray-500 dark:text-gray-400 text-sm">Total abonnés</p>
          <h3 className="text-3xl font-bold dark:text-white">{stats.total}</h3>
        </div>

        <div className="p-5 rounded-lg shadow bg-white dark:bg-gray-900">
          <p className="text-gray-500 dark:text-gray-400 text-sm">Approuvés</p>
          <h3 className="text-3xl font-bold text-green-600">{stats.approved}</h3>
        </div>

        <div className="p-5 rounded-lg shadow bg-white dark:bg-gray-900">
          <p className="text-gray-500 dark:text-gray-400 text-sm">En attente</p>
          <h3 className="text-3xl font-bold text-yellow-500">{stats.pending}</h3>
        </div>
      </div>

      
      <div className="bg-white dark:bg-gray-900 p-4 rounded-lg shadow h-[250px] sm:h-[350px]">
        <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />
      </div>

    </div>
  );
}
